import { DefinedVariable } from "./allData";
import { toast } from "@components/ui/use-toast";
import {
  Type,
  Hash,
  CheckSquare,
  AlignJustify,
  Layers,
  TextCursorInput,
  Link,
  Globe,
  Cpu,
  Lock,
  Calendar,
  MapPin,
  FlaskConical,
  Telescope,
} from "lucide-react";

// Validate body content based on raw body type
export const validateBodyContent = (
  content: string,
  type: string,
): { isValid: boolean; error: string | null } => {
  if (!content || !content.trim()) {
    return { isValid: true, error: null };
  }

  // Handles patterns like {{variable}}, {{config.key}}, {{data.id}}, etc.
  const replaceTemplateVars = (
    str: string,
    replaceWith: string = "placeholder",
  ) => {
    return str.replace(/\{\{[^}]+\}\}/g, (match, offset) => {
      // Get context around the match
      const beforeMatch = str.substring(0, offset);
      const afterMatch = str.substring(offset + match.length);

      // If wrapped in array brackets like [{{var}}], treat as array placeholder
      if (
        beforeMatch.trimEnd().endsWith("[") &&
        afterMatch.trimStart().startsWith("]")
      ) {
        return '"placeholder_item"';
      }

      // Check if this is a JSON value context (after a colon)
      // Pattern: "key": {{variable}} or "key":{{variable}}
      const jsonValuePattern = /:\s*$/;
      if (jsonValuePattern.test(beforeMatch.trimEnd())) {
        // It's a JSON value - use valid JSON placeholder (could be string, array, or object)
        // Use an array as it's the most flexible placeholder
        return '["placeholder"]';
      }

      // If inside quotes, use quoted placeholder
      const lastQuote = beforeMatch.lastIndexOf('"');
      const lastEscape = beforeMatch.lastIndexOf("\\");
      if (lastQuote > lastEscape && afterMatch.trimStart().startsWith('"')) {
        return replaceWith;
      }

      // Default: use quoted placeholder for safety
      return `"${replaceWith}"`;
    });
  };

  switch (type) {
    case "json":
      try {
        const testContent = replaceTemplateVars(content, "placeholder");
        JSON.parse(testContent);
        return { isValid: true, error: null };
      } catch (e: any) {
        return { isValid: false, error: `Invalid JSON format.` };
      }

    case "xml":
      try {
        const testContent = replaceTemplateVars(content, "placeholder");
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(testContent, "text/xml");
        const parserError = xmlDoc.querySelector("parsererror");
        if (parserError) {
          return { isValid: false, error: "Invalid XML format." };
        }
        return { isValid: true, error: null };
      } catch (e) {
        return { isValid: false, error: "Invalid XML format." };
      }

    case "html":
      try {
        let testContent = replaceTemplateVars(content, "placeholder");

        // Check for XML declaration - reject if found
        if (testContent.trim().startsWith("<?xml")) {
          return {
            isValid: false,
            error:
              "XML content detected. Please use 'XML' type instead of 'HTML'.",
          };
        }

        // Check for HTTP response line and headers
        if (testContent.trim().startsWith("HTTP/")) {
          const bodyStart = testContent.indexOf("\r\n\r\n");
          if (bodyStart !== -1) {
            testContent = testContent.substring(bodyStart + 4);
          } else {
            const bodyStartLF = testContent.indexOf("\n\n");
            if (bodyStartLF !== -1) {
              testContent = testContent.substring(bodyStartLF + 2);
            }
          }
        }

        // Check if content looks like XML (custom root element without HTML tags)
        const trimmedContent = testContent.trim();
        const hasHtmlTags =
          /<(html|head|body|div|span|p|h[1-6]|a|img|ul|ol|li|table|form|input|button|nav|header|footer|section|article)/i.test(
            trimmedContent,
          );
        const startsWithCustomTag = /^<[a-z][a-z0-9]*(?:\s|>)/i.test(
          trimmedContent,
        );
        const hasAnyTags = /<[a-z][a-z0-9]*(?:\s|>|\/)/i.test(trimmedContent);

        // Require HTML to have at least some tags
        if (!hasAnyTags) {
          return { isValid: false, error: "Invalid HTML format." };
        }

        if (
          startsWithCustomTag &&
          !hasHtmlTags &&
          !trimmedContent.toLowerCase().includes("<!doctype")
        ) {
          return {
            isValid: false,
            error:
              "XML-like structure detected. Please use 'XML' type for custom tags.",
          };
        }

        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(testContent, "text/html");
        const parserError = htmlDoc.querySelector("parsererror");
        if (parserError) {
          return { isValid: false, error: "Invalid HTML format." };
        }
        return { isValid: true, error: null };
      } catch (e) {
        return { isValid: false, error: "Invalid HTML format." };
      }

    case "javascript":
      // Basic syntax check for JavaScript
      try {
        // Replace template variables with valid strings
        const testContent = replaceTemplateVars(content, '"placeholder"');
        // Check for basic syntax errors
        new Function(testContent);
        return { isValid: true, error: null };
      } catch (e: any) {
        return { isValid: false, error: `Invalid JavaScript syntax.` };
      }

    case "text":
    default:
      return { isValid: true, error: null };
  }
};

// Validate that array/object variables are used correctly in JSON bodies
export const validateVariableUsageInJSON = (
  jsonString: string,
  definedVars: DefinedVariable[] = [],
): { isValid: boolean; error: string | null } => {
  if (!jsonString || !jsonString.trim()) {
    return { isValid: true, error: null };
  }

  // Find all variable references
  const varMatches = [...jsonString.matchAll(/\{\{\s*([^}]+)\s*\}\}/g)];

  for (const [fullMatch, varName] of varMatches) {
    const trimmedName = varName.trim();

    // Skip config variables and array access patterns
    if (trimmedName.startsWith("config.") || trimmedName.includes("[")) {
      continue;
    }

    // Find the variable definition
    const variable = definedVars.find((v) => v.name === trimmedName);

    if (!variable) continue;

    // Check if variable value is an array or object
    const isArrayOrObject =
      typeof variable.value === "object" && variable.value !== null;

    if (isArrayOrObject) {
      // Find the context around this variable in the JSON
      const matchIndex = jsonString.indexOf(fullMatch);
      const before = jsonString.substring(0, matchIndex).trim();
      const after = jsonString.substring(matchIndex + fullMatch.length).trim();

      // Check if wrapped in extra brackets like: [{{arrayVar}}]
      if (before.endsWith("[") && after.startsWith("]")) {
        const isArray = Array.isArray(variable.value);
        return {
          isValid: false,
          error: `Variable "${trimmedName}" is already ${isArray ? "an array" : "an object"}. Remove the extra brackets [ ] around {{${trimmedName}}}. Use "key": {{${trimmedName}}} instead of "key": [{{${trimmedName}}}].`,
        };
      }
    }
  }

  return { isValid: true, error: null };
};

export const validateLoopingFromUrl = (
  url: string,
  definedVariables: DefinedVariable[],
) => {
  if (!/{{\s*[^{}]+\s*}}/.test(url)) {
    return { isValid: true, url };
  }

  let resolvedUrl = url;
  const matches = [...url.matchAll(/{{\s*([^{}]+)\s*}}/g)];

  for (const [, varName] of matches) {
    const trimmed = varName.trim();

    const arrayMatch = trimmed.match(/^(\w+)\[(\*|\d+)\]\.(\w+)$/);
    if (arrayMatch) {
      const [, arrayName, indexOrStar, key] = arrayMatch;
      const variable = definedVariables.find((v) => v.name === arrayName);

      if (!variable)
        return {
          isValid: false,
          error: `Variable '${arrayName}' not found.`,
        };
      if (!Array.isArray(variable.value))
        return { isValid: false, error: `'${arrayName}' is not an array.` };

      if (indexOrStar === "*") {
        const values = variable.value.map((v) => v[key]);
        return {
          isValid: true,
          isLoop: true,
          loopVariable: arrayName,
          loopKey: key,
          loopValues: values,
        };
      }

      const idx = parseInt(indexOrStar);
      const item = variable.value[idx];
      if (!item || !(key in item))
        return {
          isValid: false,
          error: `Key '${key}' missing at index ${idx}`,
        };

      const value = item[key];
      resolvedUrl = resolvedUrl.replace(`{{${trimmed}}}`, value);
      continue;
    }

    // simple variable
    const simpleVar = definedVariables.find((v) => v.name === trimmed);
    if (simpleVar) {
      resolvedUrl = resolvedUrl.replace(
        `{{${trimmed}}}`,
        simpleVar.value ?? "",
      );
    }
  }

  return { isValid: true, url: resolvedUrl };
};

// Function to interpolate only defined variables
export function interpolateDefinedVariablesOnly(
  input: string,
  definedVars: DefinedVariable[] = [],
): string {
  if (!input || typeof input !== "string") return input;

  return input.replace(/{{\s*(.*?)\s*}}/g, (match, key) => {
    // Skip config variables
    if (key.startsWith("config.")) return match;

    // Handle array-style access: values[1].id
    const arrayMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
    if (arrayMatch) {
      const [, arrayName, indexStr, prop] = arrayMatch;

      const candidates = definedVars.filter(
        (v) =>
          v.name === arrayName && Array.isArray(v.value) && v.value.length > 0,
      );

      if (!candidates.length) return match;

      const variable = candidates[0];
      const index = parseInt(indexStr, 10);
      if (!variable.value) return match;
      const item = variable.value[index];

      if (!item || typeof item !== "object" || !(prop in item)) return match;
      return String(item[prop]);
    }

    const candidates = definedVars.filter(
      (v) =>
        v.name === key &&
        v.value !== undefined &&
        v.value !== null &&
        String(v.value).trim() !== "",
    );

    if (!candidates.length) {
      return match;
    }

    const resolved =
      candidates.find((v) => v.isAuthentication === true) ??
      candidates.find((v) => v.variableType === "Settings") ??
      candidates[0];

    // Handle arrays and objects by using JSON.stringify
    // This allows proper JSON insertion for array/object variables
    if (typeof resolved.value === "object" && resolved.value !== null) {
      return JSON.stringify(resolved.value);
    }

    return String(resolved.value);
  });
}

// Function to map frontend auth type to backend auth type
export function mapAuthTypeToBackend(type: string) {
  switch (type) {
    case "basic":
      return "basic_auth";
    case "bearer":
      return "bearer_token";
    case "jwtBearer":
      return "jwt_token";
    case "api-key":
      return "api_key";
    case "authorization":
      return "authorization";
    default:
      return "none";
  }
}

// Add this helper function at the top of the file, after imports
export const normalizeResponseData = (data: any): any => {
  if (!data || typeof data !== "object") return data;

  // Check if it's an object with numeric keys
  if (!Array.isArray(data)) {
    const keys = Object.keys(data);
    const numericKeys = keys.filter((key) => /^\d+$/.test(key));

    // If majority of keys are numeric indices, convert to array
    if (numericKeys.length > 0 && numericKeys.length >= keys.length * 0.5) {
      const arrayData = numericKeys
        .map((key) => parseInt(key))
        .sort((a, b) => a - b)
        .map((index) => data[String(index)]);

      // Recursively normalize nested data
      return arrayData.map((item) => normalizeResponseData(item));
    }
  }

  // If it's already an array, recursively normalize items
  if (Array.isArray(data)) {
    return data.map((item) => normalizeResponseData(item));
  }

  // If it's a regular object, recursively normalize values
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      normalizeResponseData(value),
    ]),
  );
};

// function to build nested object from path
export function buildNestedObjectFromPath(path: string, value: any): any {
  const segments = path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);

  return segments.reduceRight((acc, key) => {
    const index = Number(key);
    if (!isNaN(index)) {
      const arr: any[] = [];
      arr[index] = acc;
      return arr;
    }
    return { [key]: acc };
  }, value);
}

export function getBundleTypeEnum(name: string) {
  switch (name) {
    case "Save Logs":
      return "parquet";
    case "Settings":
      return "setting";
    case "Action":
      return "action";
    case "Data List":
      return "datalist";
    case "Threat Lookup":
      return "threat_lookup";
    default:
      return name?.toLowerCase() || "action";
  }
}

// Helper function to sort logic types consistently
export const sortLogicTypes = (logicTypes: any[]) => {
  const order = ["Action", "Save Logs", "Settings"];

  return logicTypes.sort((a, b) => {
    const aIndex = order.indexOf(a.name);
    const bIndex = order.indexOf(b.name);

    // If both are in predefined order, use that order
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

    // Predefined types come first
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // For Data List and Threat Lookup, sort by minimum bundleId
    const aMinId = a.data?.[0]?.id || Infinity;
    const bMinId = b.data?.[0]?.id || Infinity;

    return aMinId - bMinId;
  });
};

export function sanitizeParams(input: string): string {
  if (!input) return "";

  // Alphanumeric only
  const plainPattern = /^[a-zA-Z0-9]+$/;

  // {{variable}} or {{data.value}}
  const templatePattern = /^\{\{[a-zA-Z0-9]+(\.[a-zA-Z0-9]+)*\}\}$/;

  const isValid = plainPattern.test(input) || templatePattern.test(input);

  if (!isValid) {
    toast({
      variant: "destructive",
      title: "Invalid Format",
      description:
        "Only alphanumeric or {{variable}} / {{config.value}} / {{action.value}}format allowed",
      duration: 3000,
    });
    return "";
  }

  return input;
}

export const getFieldIconbyFieldType = (fieldType: string) => {
  switch (fieldType) {
    case "input_text":
    case "text":
    case "domain":
      return Type;

    case "input_number":
    case "number":
    case "port":
      return Hash;

    case "boolean":
    case "checkbox":
      return CheckSquare;

    case "select":
    case "dropdown":
    case "radio":
      return AlignJustify;

    case "multiselect":
    case "multi_select_dropdown":
      return Layers;

    case "textarea":
    case "input_textarea":
      return TextCursorInput;

    case "url":
      return Link;

    case "ip":
    case "host_address":
      return Globe;

    case "mac":
      return Cpu;

    case "input_password":
      return Lock;

    case "input_date":
      return Calendar;

    case "region_country":
      return MapPin;

    case "testConnectionBtn":
      return FlaskConical;

    case "proxy":
      return Telescope;

    default:
      return Type;
  }
};
